// @flow
import React from "react"
import { connect, type Connector } from "react-redux"
import { bindActionCreators } from "redux"
import classnames from "classnames"
import styled from "styled-components"
import type { ThemeProps } from "@kiwicom/nitro/lib/records/Theme"
import defaultTheme from "@kiwicom/orbit-components/lib/defaultTheme"

import * as helpCenterActions from "common/scenes/HelpCenter/services/helpCenterDuck"
import * as modalsActions from "common/services/modals/actions"
import cuckoo from "common/services/cuckoo/cuckoo"
import Translate from "./Translate"
import { isKiwiBrandingSelector } from "common/services/branding/selectors"
import { delayedChatSelector } from "common/services/remoteConfig/index"
import { openHelp } from "common/modules/SmartFAQ/services/SmartFAQDuck"

const helpCenterPaths = [
  "helpCenter",
  "helpCenterSearch",
  "helpCenterArticle",
  "helpCenterCategory",
  "helpCenterArticleSearch",
]

const elevioSize = 56
const elevioOffset = elevioSize / 2

const ChatIcon = styled.a`
  position: fixed;
  right: ${elevioOffset}px;
  bottom: ${elevioOffset * 2.5}px;
  z-index: 6000;

  display: block;
  width: ${elevioSize}px;
  height: ${elevioSize}px;
  border-radius: ${elevioSize / 2}px;

  color: white;
  background-color: ${({ theme }: ThemeProps) => theme.orbit.paletteProductNormal};
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: width 0.3s, border-radius 0.3s cubic-bezier(0.55, 0, 0.1, 1);
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.32);
  :hover,
  :active,
  :focus {
    color: white;
  }
`

ChatIcon.defaultProps = {
  theme: defaultTheme,
}

type OwnProps = {}

type Props = {
  ...OwnProps,
  page: any,
  isSimpleLandingPage: boolean,
  delayedChat: any,
  maintenance: boolean,
  isPartner: boolean,
  isMMBPage: boolean,
  actions: any,
  openHelpCenter: () => void,
  isPartnerChatEnabled: boolean,
}

type State = {
  open: boolean,
  hover: boolean,
}

class Chat extends React.Component<Props, State> {
  state = {
    open: false,
    hover: false,
  }

  getChatType = () => {
    const { page } = this.props
    return helpCenterPaths.find(path => path === page.get("name")) && "Chat-HelpCenter"
  }

  handleOpenChat = () => {
    const { actions, delayedChat } = this.props

    actions.openChat("sideBar", delayedChat.showDelayedChat)
  }

  handleOpen = () => {
    const { isPartner, isMMBPage, isPartnerChatEnabled } = this.props
    const { open, hover } = this.state
    if (isMMBPage && isPartner && isPartnerChatEnabled) {
      this.setState({ open: !open, hover: !hover })
    } else {
      this.handleOpenSideBar()
    }
  }

  handleEnter = () => {
    this.setState({ hover: true })
    return this.state
  }

  handleEnter: () => void // because of this bind

  handleLeave = () => {
    const { open } = this.state
    if (!open) {
      this.setState({ hover: false })
    }
  }

  handleOpenSideBar = () => {
    const { openHelpCenter } = this.props
    if (typeof window !== "undefined") {
      openHelpCenter()
      cuckoo.infinario("helpCenterAction", { action: "helpOpenRequest", specify: "chat" })
      cuckoo.infinario("helpCenterAction", { action: "helpOpened", specify: "widget" })
      this.setState({ open: false })
    }
  }

  render() {
    const chatType = this.getChatType()
    const { open, hover } = this.state
    const { isSimpleLandingPage, maintenance } = this.props
    return (
      (!isSimpleLandingPage || maintenance) && (
        <ChatIcon
          id="chat-icon"
          data-test="chat-icon"
          className={classnames("spShadow-2", chatType, { open, in: hover })}
          onClick={this.handleOpen}
          onMouseOver={this.handleEnter}
          onMouseOut={this.handleLeave}
        >
          <div className="chat-container">
            <div className="chat-icon-bg">
              <i className="fa fa-question" />
            </div>
            <div className="chat-label">
              <span>
                <Translate tKey="common.elevio.need_help" />
              </span>
            </div>
          </div>
          <ul
            data-test="chat-menu"
            className={classnames("chat-menu", "spShadow-2", { "menu-open": open })}
          >
            <li onClick={this.handleOpenSideBar} data-test="chat-menu-need-help">
              <i className="fa fa-question" />
              <div className="text">
                <Translate tKey="help_center.open_original" />
              </div>
            </li>
            <li onClick={this.handleOpenChat} data-test="chat-menu-chat">
              <i className="fa fa-wechat" />
              <div className="text">
                <Translate tKey="booking.abandonment.help.chat_header" />
              </div>
            </li>
            <li onClick={this.handleLeave} data-test="chat-menu-close">
              <i className="fa fa-close" />
              <div className="text">
                <Translate tKey="common.close" />
              </div>
            </li>
          </ul>
        </ChatIcon>
      )
    )
  }
}

const connector: Connector<OwnProps, Props> = connect(
  state => ({
    page: state.page,
    isSimpleLandingPage: state.options.isRootPage && isKiwiBrandingSelector(state),
    delayedChat: delayedChatSelector(state),
    maintenance: Boolean(state.modals.get("maintenance")),
    isPartner: Boolean(state.user.user && state.user.user.affiliateId),
    isMMBPage: ["manage", "account"].includes(state.page.get("namesPath")),
    isPartnerChatEnabled: state.splitster.get("MMB_PARTNER_CHAT").value === "enabled",
  }),
  dispatch => ({
    actions: bindActionCreators({ ...helpCenterActions, ...modalsActions }, dispatch),
    openHelpCenter: () => dispatch(openHelp()),
  }),
)

export default connector(Chat)
